import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, X, Download, Monitor, Tablet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, apiClient } from "@/lib/api";

// Generate a simple device fingerprint
const getDeviceFingerprint = () => {
  const ua = navigator.userAgent;
  const screen = `${window.screen.width}x${window.screen.height}`;
  const platform = navigator.platform;
  return btoa(`${ua}-${screen}-${platform}`).slice(0, 32);
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipad|ipod/.test(ua)) {
    if (/ipad|tablet/.test(ua) || (window.innerWidth >= 768 && window.innerWidth < 1024)) {
      return 'tablet';
    }
    return 'mobile';
  }
  return 'desktop';
};

export default function PWAInstallBanner() {
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isNewDevice, setIsNewDevice] = useState(false);
  const [previousDevices, setPreviousDevices] = useState([]);
  const [currentDeviceType, setCurrentDeviceType] = useState('desktop');

  // Check if user has completed setup
  const hasBrandSetup = user?.brand_voice || user?.brand_url;
  const hasBrandType = user?.brand_type;
  const hasFacebook = user?.social_connections?.facebook?.connected;
  const hasInstagram = user?.social_connections?.instagram?.connected;
  const hasTikTok = user?.social_connections?.tiktok?.connected;
  const hasAnySocialConnected = hasFacebook || hasInstagram || hasTikTok;
  const hasSkippedSocial = user?.skipped_social_setup;
  const setupComplete = hasBrandSetup && hasBrandType && (hasAnySocialConnected || hasSkippedSocial);

  useEffect(() => {
    // Don't show PWA banner until setup is complete
    if (!setupComplete || !user?.id) return;
    
    const deviceFingerprint = getDeviceFingerprint();
    const deviceType = getDeviceType();
    setCurrentDeviceType(deviceType);
    
    // Check if already installed or dismissed on THIS device
    const dismissedDevices = JSON.parse(localStorage.getItem("viraly_pwa_dismissed_devices") || "{}");
    const dismissedTime = dismissedDevices[deviceFingerprint] || 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    // Check if running in standalone mode (already installed)
    const standalone = window.matchMedia("(display-mode: standalone)").matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    
    if (standalone) {
      // Record that this device has PWA installed
      recordDeviceInstall(deviceFingerprint, deviceType);
      return;
    }
    
    // Check for iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);
    
    // Check if this is a new device (user has installed on other devices)
    checkForNewDevice(deviceFingerprint, deviceType, daysSinceDismissed);
    
    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner after a short delay if not dismissed recently
      if (daysSinceDismissed > 7) {
        setTimeout(() => setShowBanner(true), 3000);
      }
    };
    
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    
    // Also check if deferredPrompt was already captured in App.js
    if (window.deferredPrompt && daysSinceDismissed > 7) {
      setDeferredPrompt(window.deferredPrompt);
      setTimeout(() => setShowBanner(true), 3000);
    }
    
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    };
  }, [setupComplete, user?.id]);

  const checkForNewDevice = async (fingerprint, deviceType, daysSinceDismissed) => {
    try {
      // Get user's installed devices from backend
      const response = await apiClient.get("/users/pwa-devices");
      const devices = response.data?.devices || [];
      setPreviousDevices(devices);
      
      // Check if current device is already recorded
      const isKnownDevice = devices.some(d => d.fingerprint === fingerprint);
      
      if (!isKnownDevice && devices.length > 0) {
        // This is a NEW device and user has installed on other devices
        setIsNewDevice(true);
        if (daysSinceDismissed > 1) { // Show more frequently for new devices
          setTimeout(() => setShowBanner(true), 2000);
        }
      } else if (!isKnownDevice && daysSinceDismissed > 7) {
        // First time user, show normal prompt
        setTimeout(() => setShowBanner(true), 3000);
      }
    } catch (e) {
      // API not available, fall back to local storage
      const localDevices = JSON.parse(localStorage.getItem("viraly_pwa_devices") || "[]");
      const isKnownDevice = localDevices.some(d => d.fingerprint === fingerprint);
      
      if (!isKnownDevice && localDevices.length > 0) {
        setIsNewDevice(true);
        setPreviousDevices(localDevices);
        if (daysSinceDismissed > 1) {
          setTimeout(() => setShowBanner(true), 2000);
        }
      } else if (daysSinceDismissed > 7) {
        setTimeout(() => setShowBanner(true), 3000);
      }
    }
  };

  const recordDeviceInstall = async (fingerprint, deviceType) => {
    const deviceInfo = {
      fingerprint,
      type: deviceType,
      installedAt: new Date().toISOString()
    };
    
    try {
      await apiClient.post("/users/pwa-devices", deviceInfo);
    } catch (e) {
      // Fall back to local storage
      const localDevices = JSON.parse(localStorage.getItem("viraly_pwa_devices") || "[]");
      if (!localDevices.some(d => d.fingerprint === fingerprint)) {
        localDevices.push(deviceInfo);
        localStorage.setItem("viraly_pwa_devices", JSON.stringify(localDevices));
      }
    }
  };

  const handleInstall = async () => {
    const fingerprint = getDeviceFingerprint();
    
    if (isIOS) {
      // For iOS, show instructions
      setShowBanner(false);
      // The banner already shows iOS instructions
      return;
    }
    
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        recordDeviceInstall(fingerprint, currentDeviceType);
        setShowBanner(false);
        setDeferredPrompt(null);
      }
    }
  };

  const handleDismiss = () => {
    const fingerprint = getDeviceFingerprint();
    const dismissedDevices = JSON.parse(localStorage.getItem("viraly_pwa_dismissed_devices") || "{}");
    dismissedDevices[fingerprint] = Date.now();
    localStorage.setItem("viraly_pwa_dismissed_devices", JSON.stringify(dismissedDevices));
    setShowBanner(false);
  };

  // Don't render if already installed
  if (isStandalone) return null;

  // Get device icon
  const DeviceIcon = currentDeviceType === 'mobile' ? Smartphone : 
                     currentDeviceType === 'tablet' ? Tablet : Monitor;

  // Get message based on context
  const getInstallMessage = () => {
    if (isNewDevice && previousDevices.length > 0) {
      const otherDeviceTypes = previousDevices.map(d => d.type).filter(t => t !== currentDeviceType);
      const otherDevice = otherDeviceTypes[0] || 'another device';
      return (
        <>
          <h3 className="font-heading font-bold text-lg mb-1">
            Install Viraly on this {currentDeviceType}
          </h3>
          <p className="text-sm text-viraly-muted">
            We noticed you've installed Viraly on your {otherDevice}. 
            Install here too for quick access from your {currentDeviceType}!
          </p>
        </>
      );
    }
    
    if (isIOS) {
      return (
        <>
          <h3 className="font-heading font-bold text-lg mb-1">
            Install Viraly App
          </h3>
          <p className="text-sm text-viraly-muted">
            Tap <span className="inline-flex items-center"><svg className="w-4 h-4 mx-1" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3 3h-2v8h-2V5H9l3-3zm-7 9v11h14V11h-4v2h2v7H7v-7h2v-2H5z"/></svg></span> then "Add to Home Screen" for quick access
          </p>
        </>
      );
    }
    
    return (
      <>
        <h3 className="font-heading font-bold text-lg mb-1">
          Install Viraly App
        </h3>
        <p className="text-sm text-viraly-muted">
          Get faster access and work offline. No app store needed!
        </p>
      </>
    );
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-6"
        >
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-viraly-orange/20 via-viraly-card to-viraly-orange/10 border border-viraly-orange/30 p-4 md:p-5">
            {/* Background glow */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-viraly-orange/20 rounded-full blur-3xl" />
            
            {/* New device badge */}
            {isNewDevice && (
              <div className="absolute top-2 right-2 bg-viraly-orange text-white text-xs px-2 py-1 rounded-full font-medium">
                New Device
              </div>
            )}
            
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-viraly-gradient flex items-center justify-center flex-shrink-0">
                  <DeviceIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  {getInstallMessage()}
                </div>
              </div>
              
              <div className="flex items-center gap-2 w-full md:w-auto">
                {!isIOS && (
                  <Button
                    onClick={handleInstall}
                    className="btn-primary flex-1 md:flex-none flex items-center justify-center gap-2 px-5"
                    data-testid="pwa-install-btn"
                  >
                    <Download className="w-4 h-4" />
                    Install
                  </Button>
                )}
                <Button
                  onClick={handleDismiss}
                  variant="ghost"
                  className="text-viraly-muted hover:text-white px-3"
                  data-testid="pwa-dismiss-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
